import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getApiUrl } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Download, Loader2, Maximize2, Minimize2, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import { digitsOnly, isCpfOuCnpjValido, nomeCompletoMatches } from '@/utils/cpfPublic';

function apiBase() {
  return getApiUrl() || (typeof window !== 'undefined' ? window.location.origin : '');
}

/** Controles + iframe do PDF — usado no painel desktop (duas colunas) e no Dialog mobile. */
function AssinaDocumentViewerPanel({
  titulo,
  documentosConvite,
  docIndice,
  setDocIndice,
  docDownloading,
  onBaixar,
  onToggleFullscreen,
  fsActive,
  viewerRef,
  documentoUrl,
  tokenDecoded,
  className,
  showTitle = true,
}) {
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col gap-3', className)}>
      {showTitle && (
        <div className="shrink-0 space-y-1 text-left">
          <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">{titulo || 'Documento para assinatura'}</h2>
        </div>
      )}

      {documentosConvite.length > 1 && (
        <div className="flex shrink-0 items-center justify-between gap-2 text-sm text-muted-foreground">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={docIndice <= 0}
            onClick={() => setDocIndice((i) => Math.max(0, i - 1))}
            aria-label="Documento anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-0 truncate text-center font-medium text-foreground">
            {documentosConvite[docIndice]?.nome || `Documento ${docIndice + 1}`} ({docIndice + 1} de{' '}
            {documentosConvite.length})
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={docIndice >= documentosConvite.length - 1}
            onClick={() => setDocIndice((i) => Math.min(documentosConvite.length - 1, i + 1))}
            aria-label="Próximo documento"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="flex shrink-0 flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="gap-2"
          disabled={docDownloading}
          onClick={() => void onBaixar()}
        >
          {docDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Baixar
        </Button>
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => void onToggleFullscreen()}>
          {fsActive ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          {fsActive ? 'Sair da tela cheia' : 'Tela cheia'}
        </Button>
      </div>

      <div
        ref={viewerRef}
        className="flex min-h-[min(52vh,28rem)] flex-1 flex-col overflow-hidden rounded-md border border-slate-200 bg-slate-100 lg:min-h-0"
      >
        <iframe
          key={`${docIndice}-${tokenDecoded}`}
          title={documentosConvite[docIndice]?.nome || 'Documento'}
          src={documentoUrl(docIndice, false)}
          className="h-[min(68vh,780px)] w-full flex-1 border-0 bg-white lg:h-full lg:min-h-[min(60dvh,40rem)]"
        />
      </div>
    </div>
  );
}

export default function AssinaConvitePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  /** 'assinatura' | 'rejeicao' — define o texto da tela final após sucesso */
  const [doneOutcome, setDoneOutcome] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [cpf, setCpf] = useState('');
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docIndice, setDocIndice] = useState(0);
  const [docDownloading, setDocDownloading] = useState(false);
  const [fsActive, setFsActive] = useState(false);
  const viewerDesktopRef = useRef(null);
  const viewerModalRef = useRef(null);

  const tokenDecoded = decodeURIComponent(token || '');
  const documentosConvite = meta && Array.isArray(meta.documentos) ? meta.documentos : [];

  const documentoUrl = (indice, download) => {
    const q = new URLSearchParams({ indice: String(indice) });
    if (download) q.set('download', '1');
    return `${apiBase()}/api/assina/public/convite/${encodeURIComponent(tokenDecoded)}/documento?${q}`;
  };

  const onFsChange = useCallback(() => {
    setFsActive(Boolean(document.fullscreenElement));
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [onFsChange]);

  useEffect(() => {
    if (docModalOpen && documentosConvite.length === 0) setDocModalOpen(false);
  }, [docModalOpen, documentosConvite.length]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const closeModalOnDesktop = () => {
      if (mq.matches) setDocModalOpen(false);
    };
    closeModalOnDesktop();
    mq.addEventListener('change', closeModalOnDesktop);
    return () => mq.removeEventListener('change', closeModalOnDesktop);
  }, []);

  const viewerElForFullscreen = () => {
    if (typeof window === 'undefined') return null;
    if (window.matchMedia('(min-width: 1024px)').matches) return viewerDesktopRef.current;
    return viewerModalRef.current;
  };

  const toggleFullscreen = async () => {
    const el = viewerElForFullscreen();
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      /* ignore */
    }
  };

  const baixarDocumento = async () => {
    setDocDownloading(true);
    try {
      const res = await fetch(documentoUrl(docIndice, true), { credentials: 'include' });
      if (!res.ok) throw new Error('Falha ao baixar');
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition');
      let name = documentosConvite[docIndice]?.nome || 'documento';
      const m = cd && /filename="?([^";]+)"?/i.exec(cd);
      if (m?.[1]) name = m[1].trim();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* silencioso: fluxo público */
    } finally {
      setDocDownloading(false);
    }
  };

  const identidadeOk =
    nomeCompleto.trim().length >= 3 && isCpfOuCnpjValido(cpf);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const t = decodeURIComponent(token || '');
        const res = await fetch(`${apiBase()}/api/assina/public/convite/${encodeURIComponent(t)}`, {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 410) {
          setError('Este convite expirou.');
          setMeta(null);
        } else if (!res.ok) {
          setError(data.error || 'Convite não encontrado.');
          setMeta(null);
        } else {
          setMeta(data);
          void fetch(`${apiBase()}/api/assina/public/convite/${encodeURIComponent(t)}/visualizado`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
        }
      } catch {
        if (!cancelled) setError('Não foi possível carregar o convite.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const solicitarOtp = async () => {
    if (!identidadeOk) return;
    setBusy(true);
    setError(null);
    try {
      const t = decodeURIComponent(token || '');
      const res = await fetch(`${apiBase()}/api/assina/public/convite/${encodeURIComponent(t)}/solicitar-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome_completo: nomeCompleto.trim(),
          cpf: digitsOnly(cpf),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao enviar código');
      setOtpSent(true);
    } catch (e) {
      setError(e?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  const assinar = async () => {
    setBusy(true);
    setError(null);
    try {
      const t = decodeURIComponent(token || '');
      const res = await fetch(`${apiBase()}/api/assina/public/convite/${encodeURIComponent(t)}/assinar`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          otp,
          nome_completo: nomeCompleto.trim(),
          cpf: digitsOnly(cpf),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao assinar');
      setDoneOutcome('assinatura');
      setDone(true);
    } catch (e) {
      setError(e?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  const rejeitar = async () => {
    setBusy(true);
    setError(null);
    try {
      const t = decodeURIComponent(token || '');
      const res = await fetch(`${apiBase()}/api/assina/public/convite/${encodeURIComponent(t)}/rejeitar`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao rejeitar');
      setDoneOutcome('rejeicao');
      setDone(true);
      setMeta((m) => (m ? { ...m, signatarioStatus: 'rejeitado' } : m));
    } catch (e) {
      setError(e?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  const showDocColumn = Boolean(meta && documentosConvite.length > 0);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-4 lg:flex-row lg:items-stretch lg:justify-center lg:gap-6 lg:p-6 lg:mx-auto lg:max-w-[min(120rem,100%)]">
      <Card className="w-full max-w-md shrink-0 shadow-lg lg:self-start lg:sticky lg:top-6">
        <CardHeader className="text-center">
          <PenLine className="h-10 w-10 mx-auto text-primary mb-2" />
          <CardTitle>Assina — Auction</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && <p className="text-sm text-destructive text-center">{error}</p>}
          {meta &&
            documentosConvite.length > 0 &&
            !done &&
            meta.signatarioStatus !== 'assinado' &&
            meta.signatarioStatus !== 'rejeitado' && (
            <Button
              type="button"
              variant="outline"
              className="w-full lg:hidden"
              onClick={() => {
                setDocIndice(0);
                setDocModalOpen(true);
              }}
            >
              Visualizar documento
            </Button>
          )}
          {done && !error && (
            <div className="space-y-3 text-center">
              {doneOutcome === 'rejeicao' ? (
                <>
                  <p className="text-sm text-green-700">Operação de rejeição registrada com sucesso.</p>
                  <p className="text-sm text-muted-foreground">
                    O documento foi recusado e não seguirá para as próximas etapas de assinatura.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Se necessário, entre em contato com o responsável pelo envio.
                  </p>
                  <p className="text-sm text-muted-foreground">Você já pode fechar esta tela com segurança.</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-green-700">Operação registrada com sucesso.</p>
                  <p className="text-sm text-muted-foreground">
                    Após a confirmação da operação, aguarde a assinatura de todos os signatários para que o documento final seja
                    disponibilizado.
                  </p>
                  <p className="text-sm text-muted-foreground">Você já pode fechar esta tela com segurança.</p>
                </>
              )}
            </div>
          )}
          {meta && !done && (
            <>
              <p className="text-sm text-center text-muted-foreground">{meta.empresaNome}</p>
              <p className="text-center font-medium">{meta.titulo || 'Documento para assinatura'}</p>
              <p className="text-sm text-center">
                Olá, <strong>{meta.signatarioNome}</strong>
              </p>
              {meta.signatarioStatus === 'assinado' && (
                <p className="text-center text-sm text-green-700">Você já assinou este documento.</p>
              )}
              {meta.nivel !== 'simples' && meta.signatarioStatus !== 'assinado' && (
                <p className="text-xs text-center text-amber-700">
                  Este convite usa nível &quot;{meta.nivel}&quot;. O fluxo OTP aqui é apenas para assinatura simples; entre em contato com a empresa.
                </p>
              )}
              {meta.nivel === 'simples' && meta.signatarioStatus !== 'assinado' && meta.statusEnvelope === 'enviado' && (
                <div className="space-y-3">
                  <div className="space-y-2 text-left">
                    <div>
                      <Label htmlFor="assina-nome-completo">Nome completo (obrigatório)</Label>
                      <Input
                        id="assina-nome-completo"
                        autoComplete="name"
                        value={nomeCompleto}
                        onChange={(e) => setNomeCompleto(e.target.value)}
                        placeholder="Igual ao cadastro do convite"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="assina-cpf">CPF ou CNPJ (obrigatório)</Label>
                      <Input
                        id="assina-cpf"
                        inputMode="numeric"
                        autoComplete="off"
                        value={cpf}
                        onChange={(e) => setCpf(digitsOnly(e.target.value).slice(0, 14))}
                        placeholder="Somente números (11 ou 14 dígitos)"
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Use 11 dígitos para CPF ou 14 para CNPJ, conforme cadastrado pela empresa no envelope.
                      </p>
                    </div>
                    {nomeCompleto.trim().length >= 3 &&
                      meta.signatarioNome &&
                      !nomeCompletoMatches(meta.signatarioNome, nomeCompleto) && (
                        <p className="text-xs text-amber-700">
                          O nome não parece coincidir com o destinatário deste convite. Confira acentos e nome completo.
                        </p>
                      )}
                  </div>
                  <Button
                    className="w-full"
                    variant="secondary"
                    disabled={busy || !identidadeOk}
                    onClick={solicitarOtp}
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Receber código por e-mail'}
                  </Button>
                  {otpSent && (
                    <>
                      <div>
                        <Label>Código de 6 dígitos</Label>
                        <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} />
                      </div>
                      <Button
                        className="w-full"
                        disabled={busy || otp.length !== 6 || !identidadeOk}
                        onClick={assinar}
                      >
                        Confirmar assinatura
                      </Button>
                    </>
                  )}
                  <div className="pt-2 border-t space-y-2">
                    <Label>Motivo da rejeição (opcional)</Label>
                    <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Se não concordar, descreva aqui" />
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={busy || motivo.trim().length < 3}
                      onClick={rejeitar}
                    >
                      Rejeitar documento
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {showDocColumn && (
        <div className="mt-6 hidden min-h-0 w-full min-w-0 flex-1 flex-col rounded-xl border bg-card text-card-foreground shadow-lg lg:mt-0 lg:flex lg:max-h-[calc(100dvh-3rem)]">
          <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-6">
            <AssinaDocumentViewerPanel
              titulo={meta?.titulo}
              documentosConvite={documentosConvite}
              docIndice={docIndice}
              setDocIndice={setDocIndice}
              docDownloading={docDownloading}
              onBaixar={baixarDocumento}
              onToggleFullscreen={toggleFullscreen}
              fsActive={fsActive}
              viewerRef={viewerDesktopRef}
              documentoUrl={documentoUrl}
              tokenDecoded={tokenDecoded}
            />
          </div>
        </div>
      )}

      <Dialog
        open={docModalOpen}
        onOpenChange={(open) => {
          setDocModalOpen(open);
          if (!open && typeof document !== 'undefined' && document.fullscreenElement) {
            void document.exitFullscreen();
          }
        }}
      >
        <DialogContent className="flex max-h-[min(92vh,56rem)] w-[min(96vw,56rem)] max-w-[min(96vw,56rem)] flex-col gap-3 p-4 sm:p-6 lg:hidden">
          <DialogHeader className="shrink-0 space-y-1 text-left">
            <DialogTitle className="pr-8">{meta?.titulo || 'Documento para assinatura'}</DialogTitle>
            <DialogDescription className="sr-only">
              Visualização do documento anexado ao convite de assinatura. Use os botões para baixar ou ampliar em tela cheia.
            </DialogDescription>
          </DialogHeader>
          <AssinaDocumentViewerPanel
            titulo={meta?.titulo}
            documentosConvite={documentosConvite}
            docIndice={docIndice}
            setDocIndice={setDocIndice}
            docDownloading={docDownloading}
            onBaixar={baixarDocumento}
            onToggleFullscreen={toggleFullscreen}
            fsActive={fsActive}
            viewerRef={viewerModalRef}
            documentoUrl={documentoUrl}
            tokenDecoded={tokenDecoded}
            showTitle={false}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
